import { c as _c } from "react/compiler-runtime";
import { getLogger } from '@onefootprint/logger';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useMutation } from '@tanstack/react-query';
import { toAsciiFileName } from '../utils/ascii-file-name';
const {
  logWarn
} = getLogger({
  location: 'percy-chat-files'
});
export type ChatFile = {
  path: string;
  mime_type: string;
};
export const useGetFileContent = session => {
  const $ = _c(2);
  let t0;
  if ($[0] !== session) {
    t0 = {
      mutationFn: async path => {
        const encodedPath = path.split("/").map(encodeURIComponent).join("/");
        const url = `${session.modalBaseUrl}/sessions/${session.sessionId}/files/${encodedPath}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${session.sessionToken}`
          }
        });
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }
        const mimeType = response.headers.get("content-type") ?? "application/octet-stream";
        const blob = await response.blob();
        return {
          blob,
          mimeType
        };
      }
    };
    $[0] = session;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const terminateChatSession = async (session: ChatSessionResponse): Promise<void> => {
  const {
    modalBaseUrl,
    sessionId,
    sessionToken
  } = session;
  const response = await fetch(`${modalBaseUrl}/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${sessionToken}`
    }
  });
  if (!response.ok) throw new Error(`Failed to terminate session (${response.status})`);
};
export type UploadChatFileResult = {
  ok: true;
  path: string;
  size: number;
  mime_type: string;
} | {
  ok: false;
  reason: string;
};
export const uploadChatFile = async (session: ChatSessionResponse, file: File): Promise<UploadChatFileResult> => {
  const {
    modalBaseUrl,
    sessionId,
    sessionToken
  } = session;
  const url = `${modalBaseUrl}/sessions/${sessionId}/files/${encodeURIComponent(toAsciiFileName(file.name))}`;
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${sessionToken}`
      },
      body: formData
    });
    if (!response.ok) {
      // No file.name: user-chosen filenames can carry PII and the logs index is broadly readable.
      logWarn('Percy file upload rejected', undefined, {
        status: response.status,
        mimeType: file.type,
        sizeBytes: file.size
      });
      return {
        ok: false,
        reason: `HTTP ${response.status}`
      };
    }
    const body = (await response.json()) as {
      path: string;
      size: number;
      mime_type: string;
    };
    return {
      ok: true,
      ...body
    };
  } catch (err) {
    logWarn('Percy file upload network error', err, {
      mimeType: file.type,
      sizeBytes: file.size
    });
    return {
      ok: false,
      reason: 'network error'
    };
  }
};